import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Delete, EditOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Checkbox, DeprecatedIconButton, Stack, Tooltip } from '@lyearn/ui';
import UserDetail, { UserDetailsOverrides } from '@/components/UsersSearch/components/UserDetail';
import { UserFragment } from '@/graphql/fragments/generated/User';

import { disabledCheckbox } from './styles';

const CLASS_OVERRIDES: UserDetailsOverrides = {
  Icon: { size: 48 },
  Name: {
    Typography: {
      color: 'text-text-primary',
      variant: 'body-short-01',
    },
  },
  Email: {
    Typography: {
      variant: 'body-short-02',
    },
  },
};

function UserCard({
  className,
  disabled,
  isSelected,
  user,
  onAction = {},
}: {
  className?: string;
  disabled?: boolean;
  isSelected?: boolean;
  user: UserFragment;
  onAction?: {
    onDelete?: (id: string) => void;
    onReplace?: () => void;
    onSelect?: (id: string) => void;
  };
}) {
  const { t } = useTranslation();
  const { onDelete, onReplace, onSelect } = onAction;

  return (
    <Stack
      alignItems="center"
      className={cn(className, 'group', { 'cursor-pointer': !!onSelect && !disabled })}
      justifyContent="between"
      onClick={onSelect && !disabled ? () => onSelect(user.id) : undefined}>
      <UserDetail className="" isSelected={isSelected} overrides={CLASS_OVERRIDES} user={user} />
      <Stack className="space-s-8">
        {onSelect ? (
          <Checkbox
            checked={isSelected}
            className={disabled ? disabledCheckbox : ''}
            data-attr={`SELECT-${user.id}`}
            data-id={user.id}
            disabled={disabled}
            variant="square"
          />
        ) : null}
        {onReplace && !disabled ? (
          <Tooltip placement="top" title={`${t('Common:Actions.Edit')}`}>
            <DeprecatedIconButton
              appearance="secondary"
              className="opacity-0 transition duration-300 ease-linear group-hover:opacity-100"
              variant="ghost"
              onClick={onReplace}>
              <EditOutline />
            </DeprecatedIconButton>
          </Tooltip>
        ) : null}
        {onDelete && !disabled ? (
          <Tooltip placement="top" title={`${t('Common:Actions.Delete')}`}>
            <DeprecatedIconButton
              appearance="secondary"
              className="opacity-0 transition duration-300 ease-linear group-hover:opacity-100"
              variant="ghost"
              onClick={() => onDelete(user.id)}>
              <Delete />
            </DeprecatedIconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Stack>
  );
}

export default React.memo(UserCard);
